import React, { Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { NavLink, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { clsx } from 'clsx'
import {
  XMarkIcon,
  HomeIcon,
  DocumentTextIcon,
  FolderIcon,
  CurrencyDollarIcon,
  BanknotesIcon,
  ChartBarIcon,
  CogIcon,
  CloudArrowUpIcon,
  WalletIcon,
  StarIcon,
  UsersIcon,
} from '@heroicons/react/24/outline'
import { useAuth } from '../../contexts/AuthContext'

const sidebarBg = {
  background: `
    radial-gradient(1200px 900px at 10% 10%, rgba(31,159,147,.18), transparent 55%),
    linear-gradient(180deg, #12191f, #0f151b)
  `,
}

const Sidebar = ({ open, setOpen }) => {
  const location = useLocation()
  const { user } = useAuth()
  const { t } = useTranslation('common')

  const SidebarContent = () => {
    const navigation = [
      { name: t('nav.dashboard'), href: '/dashboard', icon: HomeIcon },
      { name: t('nav.documents'), href: '/dashboard/documents', icon: DocumentTextIcon },
      { name: t('nav.upload'), href: '/dashboard/documents/upload', icon: CloudArrowUpIcon },
      { name: t('nav.categories'), href: '/dashboard/categories', icon: FolderIcon },
      { name: t('nav.expenses'), href: '/dashboard/budget/expenses', icon: CurrencyDollarIcon },
      { name: t('nav.incomes'), href: '/dashboard/budget/incomes', icon: BanknotesIcon },
      { name: t('nav.budgets'), href: '/dashboard/budget/budgets', icon: WalletIcon },
      { name: t('nav.goals'), href: '/dashboard/budget/goals', icon: StarIcon },
      { name: t('nav.reports'), href: '/dashboard/budget/reports', icon: ChartBarIcon },
    ]

    const adminNavigation = user?.role === 'admin'
      ? [
          { name: t('nav.settings'), href: '/dashboard/settings', icon: CogIcon },
          { name: t('nav.adminUsers'), href: '/dashboard/admin/users', icon: UsersIcon },
        ]
      : []

    const isActive = (href) => {
      if (href === '/dashboard') return location.pathname === '/dashboard'
      if (!location.pathname.startsWith(href)) return false
      const allRoutes = navigation
      const moreSpecificMatch = allRoutes.some(
        item => item.href !== href && item.href.startsWith(href) && location.pathname.startsWith(item.href)
      )
      return !moreSpecificMatch
    }

    return (
    <div className="flex h-full flex-col gap-[18px] p-[22px_18px]">
      {/* Brand */}
      <div className="flex items-center gap-[10px] px-[10px] py-[8px] rounded-xl">
        <img src="/logo.png" alt="Finomi" className="w-[34px] h-[34px] rounded-[10px] flex-shrink-0 object-contain" />
        <div>
          <h1 className="m-0 text-[18px] tracking-[.6px] font-extrabold" style={{ color: 'rgba(255,255,255,.92)' }}>
            Finomi
          </h1>
          <small className="block mt-[2px] font-semibold text-[12px]" style={{ color: 'rgba(255,255,255,.55)' }}>
            {t('appTagline')}
          </small>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex flex-col gap-[6px] pt-[6px]">
        {navigation.map((item) => (
          <NavLink
            key={item.name}
            to={item.href}
            onClick={() => setOpen(false)}
            className={clsx(
              'flex items-center gap-3 px-3 py-3 rounded-xl no-underline transition-all duration-150 font-semibold text-[14px] outline-none',
              isActive(item.href)
                ? 'border border-[rgba(47,182,170,.28)]'
                : 'border border-transparent hover:bg-[rgba(255,255,255,.06)]'
            )}
            style={isActive(item.href) ? {
              background: 'rgba(31,159,147,.22)',
              color: 'rgba(255,255,255,.95)',
              boxShadow: '0 10px 22px rgba(0,0,0,.10)',
            } : {
              color: 'rgba(255,255,255,.78)',
            }}
          >
            <item.icon
              className="h-[18px] w-[18px] flex-shrink-0"
              style={{
                opacity: isActive(item.href) ? 0.9 : 0.55,
              }}
            />
            {item.name}
          </NavLink>
        ))}

        {adminNavigation.length > 0 && (
          <>
            <div className="h-px my-2" style={{ background: 'rgba(255,255,255,.08)' }} />
            <span className="px-3 text-[11px] font-bold uppercase tracking-wider" style={{ color: 'rgba(255,255,255,.35)' }}>
              {t('nav.admin')}
            </span>
            {adminNavigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                onClick={() => setOpen(false)}
                className={clsx(
                  'flex items-center gap-3 px-3 py-3 rounded-xl no-underline transition-all duration-150 font-semibold text-[14px] outline-none',
                  isActive(item.href)
                    ? 'border border-[rgba(47,182,170,.28)]'
                    : 'border border-transparent hover:bg-[rgba(255,255,255,.06)]'
                )}
                style={isActive(item.href) ? {
                  background: 'rgba(31,159,147,.22)',
                  color: 'rgba(255,255,255,.95)',
                  boxShadow: '0 10px 22px rgba(0,0,0,.10)',
                } : {
                  color: 'rgba(255,255,255,.78)',
                }}
              >
                <item.icon
                  className="h-[18px] w-[18px] flex-shrink-0"
                  style={{ opacity: isActive(item.href) ? 0.9 : 0.55 }}
                />
                {item.name}
              </NavLink>
            ))}
          </>
        )}
      </nav>

    </div>
    )
  }

  return (
    <>
      {/* Mobile sidebar */}
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-[rgba(15,21,27,0.80)]" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-[280px] flex-1">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                    <button
                      type="button"
                      className="-m-2.5 p-2.5"
                      onClick={() => setOpen(false)}
                    >
                      <span className="sr-only">{t('closeSidebar')}</span>
                      <XMarkIcon className="h-6 w-6 text-white" />
                    </button>
                  </div>
                </Transition.Child>

                <div
                  className="flex grow flex-col overflow-y-auto"
                  style={{
                    ...sidebarBg,
                    boxShadow: '10px 0 30px rgba(0,0,0,.08)',
                  }}
                >
                  <SidebarContent />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Desktop sidebar */}
      <div
        className="hidden lg:flex lg:flex-col lg:sticky lg:top-0 lg:h-screen lg:overflow-y-auto"
        style={{
          ...sidebarBg,
          boxShadow: '10px 0 30px rgba(0,0,0,.08)',
        }}
      >
        <SidebarContent />
      </div>
    </>
  )
}

export default Sidebar
